import axios, { AxiosInstance } from 'axios'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { accessTokenProvider } from '../lib/access-token.provider'

export interface TableColumnPreferences {
  columnVisibility?: Record<string, boolean>
  columnOrder?: string[]
}

const DEBOUNCE_MS = 1500
const DEFAULT_PREFIX = 'table-config/'

function createHttpClient(): AxiosInstance {
  const client = axios.create({ baseURL: `${window.location.origin}/admin/api/UserPreferences` })

  client.interceptors.request.use(async config => {
    const token = await accessTokenProvider.get?.()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  return client
}

export class UserPreferencesStore {
  preferences = new Map<string, string>()
  isLoaded = false

  private httpClient: AxiosInstance | undefined
  private debounceTimers = new Map<string, ReturnType<typeof setTimeout>>()

  constructor() {
    makeObservable(this, {
      preferences: observable,
      isLoaded: observable,
      loadAll: action,
    })
  }

  private getHttpClient(): AxiosInstance {
    if (!this.httpClient) {
      this.httpClient = createHttpClient()
    }
    return this.httpClient
  }

  async loadAll(prefix: string = DEFAULT_PREFIX): Promise<void> {
    try {
      const response = await this.getHttpClient().get<
        { preferenceKey: string; value: string }[]
      >('', {
        params: { prefix },
      })

      runInAction(() => {
        this.preferences.clear()
        for (const pref of response.data) {
          this.preferences.set(pref.preferenceKey, pref.value)
        }
        this.isLoaded = true
      })
    } catch {
      runInAction(() => {
        this.isLoaded = true
      })
    }
  }

  getTablePreferences(preferenceKey: string): TableColumnPreferences | undefined {
    const fullKey = `${DEFAULT_PREFIX}${preferenceKey}`
    const raw = this.preferences.get(fullKey)
    if (!raw) return undefined

    try {
      return JSON.parse(raw) as TableColumnPreferences
    } catch {
      return undefined
    }
  }

  setTablePreferences(preferenceKey: string, value: TableColumnPreferences): void {
    const fullKey = `${DEFAULT_PREFIX}${preferenceKey}`
    const json = JSON.stringify(value)

    runInAction(() => {
      this.preferences.set(fullKey, json)
    })

    this.debouncedSave(fullKey, value)
  }

  private debouncedSave(fullKey: string, value: TableColumnPreferences): void {
    const existing = this.debounceTimers.get(fullKey)
    if (existing) clearTimeout(existing)

    const timer = setTimeout(async () => {
      this.debounceTimers.delete(fullKey)
      try {
        await this.getHttpClient().put(`/${fullKey}`, value)
      } catch (error) {
        console.error('Failed to save user preference:', fullKey, error)
      }
    }, DEBOUNCE_MS)

    this.debounceTimers.set(fullKey, timer)
  }
}

export const userPreferencesStore = new UserPreferencesStore()
